import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/ui/components/ui/dropdown-menu";
import { cn } from "@repo/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Check, Clock, Crosshair, Eraser, ExternalLink, ImageOff, Loader2, MapPin, MoreHorizontal, RotateCcw, Trash2, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AuditHistoryAccordion } from "@/lib/components/admin/AuditHistoryAccordion";
import { EditableAssessment } from "@/lib/components/admin/EditableAssessment";
import { RejectDialog } from "@/lib/components/admin/RejectDialog";
import { ImageLightbox } from "@/lib/components/ImageLightbox";
import { PlaceFeatureMap } from "@/lib/components/map/PlaceFeatureMap";
import { sameCoord } from "@/lib/utils/geolocation";
import { trpc } from "@/router";

// Pending local edit to the feature's position. `null` = no edit. `move` =
// admin dragged or copied coordinates from elsewhere; `clear` = admin wants
// to null the saved position. Both are staged and require an explicit save.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function FeatureReviewPanel(t0) {
  const $ = _c(184);
  const {
    featureId,
    isApproving,
    isRejecting,
    isRepositioning,
    onApprove,
    onReject,
    onReposition,
    rejectDialogOpen,
    onOpenRejectDialog,
    onCloseRejectDialog
  } = t0;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  let t1;
  if ($[0] !== featureId) {
    t1 = trpc.features.byId.queryOptions({
      id: featureId
    });
    $[0] = featureId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: feature,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[2] !== feature?.place.id) {
    t2 = trpc.places.byIdFull.queryOptions({
      id: feature?.place.id ?? 0
    });
    $[2] = feature?.place.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(feature?.place.id);
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: fullPlace
  } = useQuery(t4);
  let t5;
  if ($[7] !== feature?.place.id) {
    t5 = trpc.places.byId.queryOptions({
      id: feature?.place.id ?? 0
    });
    $[7] = feature?.place.id;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = Boolean(feature?.place.id);
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      ...t5,
      enabled: t6
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    data: parentPlaceMeta
  } = useQuery(t7);
  const savedKey = feature?.location ? `${feature.location.latitude}:${feature.location.longitude}` : "none";
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setPicked(null);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== savedKey) {
    t9 = [savedKey];
    $[13] = savedKey;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useEffect(t8, t9);
  const hasSavedLocation = Boolean(feature?.location);
  let t10;
  bb0: {
    if (!feature || !parentPlaceMeta) {
      let t11;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = [];
        $[15] = t11;
      } else {
        t11 = $[15];
      }
      t10 = t11;
      break bb0;
    }
    let t11;
    if ($[16] !== parentPlaceMeta.location.latitude || $[17] !== parentPlaceMeta.location.longitude) {
      t11 = {
        kind: "place",
        latitude: parentPlaceMeta.location.latitude,
        longitude: parentPlaceMeta.location.longitude,
        draggable: false
      };
      $[16] = parentPlaceMeta.location.latitude;
      $[17] = parentPlaceMeta.location.longitude;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let list;
    if ($[19] !== feature || $[20] !== fullPlace?.features || $[21] !== parentPlaceMeta.location.latitude || $[22] !== parentPlaceMeta.location.longitude || $[23] !== picked || $[24] !== t11) {
      list = [t11];
      const siblings = fullPlace?.features ?? [];
      for (const sib of siblings) {
        if (sib.id === feature.id) {
          continue;
        }
        if (!sib.location) {
          continue;
        }
        list.push({
          kind: "feature",
          id: sib.id,
          latitude: sib.location.latitude,
          longitude: sib.location.longitude,
          label: sib.featureType.name,
          status: sib.status,
          draggable: false
        });
      }
      if (picked && feature.location) {
        let t12;
        if ($[26] !== feature.featureType.name || $[27] !== feature.id || $[28] !== feature.location.latitude || $[29] !== feature.location.longitude || $[30] !== feature.status) {
          t12 = {
            kind: "feature",
            id: feature.id,
            latitude: feature.location.latitude,
            longitude: feature.location.longitude,
            label: feature.featureType.name,
            status: feature.status,
            draggable: false,
            ghost: true
          };
          $[26] = feature.featureType.name;
          $[27] = feature.id;
          $[28] = feature.location.latitude;
          $[29] = feature.location.longitude;
          $[30] = feature.status;
          $[31] = t12;
        } else {
          t12 = $[31];
        }
        list.push(t12);
      }
      if (picked?.kind !== "clear") {
        let t12;
        if ($[32] !== feature.location || $[33] !== parentPlaceMeta || $[34] !== picked) {
          t12 = picked?.kind === "move" ? {
            latitude: picked.latitude,
            longitude: picked.longitude
          } : feature.location ?? {
            latitude: parentPlaceMeta.location.latitude,
            longitude: parentPlaceMeta.location.longitude
          };
          $[32] = feature.location;
          $[33] = parentPlaceMeta;
          $[34] = picked;
          $[35] = t12;
        } else {
          t12 = $[35];
        }
        const effective = t12;
        const t13 = !feature.location && !picked;
        let t14;
        if ($[36] !== effective.latitude || $[37] !== effective.longitude || $[38] !== feature.featureType.name || $[39] !== feature.id || $[40] !== feature.status || $[41] !== t13) {
          t14 = {
            kind: "feature",
            id: feature.id,
            latitude: effective.latitude,
            longitude: effective.longitude,
            label: feature.featureType.name,
            status: feature.status,
            draggable: true,
            unplaced: t13
          };
          $[36] = effective.latitude;
          $[37] = effective.longitude;
          $[38] = feature.featureType.name;
          $[39] = feature.id;
          $[40] = feature.status;
          $[41] = t13;
          $[42] = t14;
        } else {
          t14 = $[42];
        }
        list.push(t14);
      }
      $[19] = feature;
      $[20] = fullPlace?.features;
      $[21] = parentPlaceMeta.location.latitude;
      $[22] = parentPlaceMeta.location.longitude;
      $[23] = picked;
      $[24] = t11;
      $[25] = list;
    } else {
      list = $[25];
    }
    t10 = list;
  }
  const markers = t10;
  if (isLoading || !feature) {
    let t11;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = /*#__PURE__*/_jsx("div", {
        className: "flex h-full items-center justify-center p-8",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      });
      $[43] = t11;
    } else {
      t11 = $[43];
    }
    return t11;
  }
  const mediaUrl = feature.media[0]?.url ?? null;
  let t11;
  bb1: {
    if (!picked) {
      t11 = false;
      break bb1;
    }
    if (picked.kind === "clear") {
      t11 = hasSavedLocation;
      break bb1;
    }
    if (!hasSavedLocation) {
      t11 = true;
      break bb1;
    }
    let t12;
    if ($[44] !== feature.location || $[45] !== picked.latitude || $[46] !== picked.longitude) {
      t12 = sameCoord({
        latitude: picked.latitude,
        longitude: picked.longitude
      }, feature.location ?? null);
      $[44] = feature.location;
      $[45] = picked.latitude;
      $[46] = picked.longitude;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    t11 = !t12;
  }
  const isDirty = t11;
  const isClearing = picked?.kind === "clear";
  let t12;
  if ($[48] !== feature.place.id) {
    t12 = {
      plats: feature.place.id
    };
    $[48] = feature.place.id;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = /*#__PURE__*/_jsx(MapPin, {
      className: "h-3.5 w-3.5"
    });
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = /*#__PURE__*/_jsx(ExternalLink, {
      className: "h-3 w-3"
    });
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== feature.place.name || $[53] !== t12) {
    t15 = /*#__PURE__*/_jsxs(Link, {
      to: "/karta",
      search: t12,
      className: "flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-brand",
      children: [t13, feature.place.name, t14]
    });
    $[52] = feature.place.name;
    $[53] = t12;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== feature.featureType.name) {
    t16 = /*#__PURE__*/_jsx("h2", {
      className: "mt-1 text-lg font-bold text-gray-900",
      children: feature.featureType.name
    });
    $[55] = feature.featureType.name;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = /*#__PURE__*/_jsx(User, {
      className: "h-3 w-3"
    });
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  const t18 = feature.creator?.name ?? "Ok\xE4nd";
  let t19;
  if ($[58] !== t18) {
    t19 = /*#__PURE__*/_jsxs("span", {
      className: "flex items-center gap-1",
      children: [t17, t18]
    });
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = /*#__PURE__*/_jsx(Clock, {
      className: "h-3 w-3"
    });
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== feature.createdAt) {
    t21 = new Date(feature.createdAt).toLocaleDateString("sv-SE");
    $[61] = feature.createdAt;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t21) {
    t22 = /*#__PURE__*/_jsxs("span", {
      className: "flex items-center gap-1",
      children: [t20, t21]
    });
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== t19 || $[66] !== t22) {
    t23 = /*#__PURE__*/_jsxs("div", {
      className: "mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500",
      children: [t19, t22]
    });
    $[65] = t19;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t15 || $[69] !== t16 || $[70] !== t23) {
    t24 = /*#__PURE__*/_jsx("div", {
      className: "flex items-start justify-between gap-3",
      children: /*#__PURE__*/_jsxs("div", {
        className: "min-w-0",
        children: [t15, t16, t23]
      })
    });
    $[68] = t15;
    $[69] = t16;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== isApproving) {
    t25 = isApproving ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-3.5 w-3.5 animate-spin"
    }) : /*#__PURE__*/_jsx(Check, {
      className: "h-3.5 w-3.5"
    });
    $[72] = isApproving;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== isApproving || $[75] !== onApprove || $[76] !== t25) {
    t26 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      onClick: onApprove,
      disabled: isApproving,
      className: "gap-1.5 bg-green-600 hover:bg-green-700",
      children: [t25, "Godk\xE4nn funktion"]
    });
    $[74] = isApproving;
    $[75] = onApprove;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = /*#__PURE__*/_jsx(Trash2, {
      className: "h-3.5 w-3.5"
    });
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== isRejecting || $[80] !== onOpenRejectDialog) {
    t28 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      variant: "destructive",
      onClick: onOpenRejectDialog,
      disabled: isRejecting,
      className: "gap-1.5",
      children: [t27, "Avvisa"]
    });
    $[79] = isRejecting;
    $[80] = onOpenRejectDialog;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== t26 || $[83] !== t28) {
    t29 = /*#__PURE__*/_jsxs("div", {
      className: "mt-4 flex flex-wrap items-center gap-2",
      children: [t26, t28]
    });
    $[82] = t26;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t24 || $[86] !== t29) {
    t30 = /*#__PURE__*/_jsxs("header", {
      className: "sticky top-0 z-10 border-b bg-white px-5 py-4",
      children: [t24, t29]
    });
    $[85] = t24;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = /*#__PURE__*/_jsx("div", {
      className: "mb-2 text-xs font-medium uppercase tracking-wider text-gray-400",
      children: "Foto"
    });
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== mediaUrl) {
    t32 = mediaUrl ? /*#__PURE__*/_jsx("button", {
      onClick: () => setLightboxOpen(true),
      className: "block aspect-[3/4] w-44 overflow-hidden rounded border bg-gray-100 transition-opacity hover:opacity-90",
      title: "Klicka f\xF6r att f\xF6rstora",
      children: /*#__PURE__*/_jsx("img", {
        src: mediaUrl,
        alt: "Foto av funktion",
        className: "h-full w-full object-contain"
      })
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex aspect-[3/4] w-44 items-center justify-center rounded border bg-gray-50",
      children: /*#__PURE__*/_jsx(ImageOff, {
        className: "h-8 w-8 text-gray-300"
      })
    });
    $[89] = mediaUrl;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== lightboxOpen || $[92] !== mediaUrl) {
    t33 = mediaUrl && /*#__PURE__*/_jsx(ImageLightbox, {
      open: lightboxOpen,
      src: mediaUrl,
      onClose: () => setLightboxOpen(false)
    });
    $[91] = lightboxOpen;
    $[92] = mediaUrl;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== t32 || $[95] !== t33) {
    t34 = /*#__PURE__*/_jsxs("div", {
      className: "shrink-0",
      children: [t31, t32, t33]
    });
    $[94] = t32;
    $[95] = t33;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = /*#__PURE__*/_jsx(MapPin, {
      className: "h-3.5 w-3.5"
    });
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== isClearing || $[99] !== isDirty) {
    t36 = isDirty && /*#__PURE__*/_jsx("span", {
      className: cn("ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold", isClearing ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"),
      children: isClearing ? "Rensas vid sparning" : "Osparade \xE4ndringar"
    });
    $[98] = isClearing;
    $[99] = isDirty;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  let t37;
  if ($[101] !== t36) {
    t37 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-gray-400",
      children: [t35, "Position", t36]
    });
    $[101] = t36;
    $[102] = t37;
  } else {
    t37 = $[102];
  }
  let t38;
  if ($[103] !== hasSavedLocation || $[104] !== isDirty) {
    t38 = hasSavedLocation && !isDirty && /*#__PURE__*/_jsx("span", {
      className: "text-[10px] text-gray-400",
      children: "Dra mark\xF6ren f\xF6r att flytta"
    });
    $[103] = hasSavedLocation;
    $[104] = isDirty;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  let t39;
  if ($[106] !== t37 || $[107] !== t38) {
    t39 = /*#__PURE__*/_jsxs("div", {
      className: "mb-2 flex items-center justify-between gap-2",
      children: [t37, t38]
    });
    $[106] = t37;
    $[107] = t38;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  let t40;
  if ($[109] !== markers) {
    t40 = markers.length > 0 ? /*#__PURE__*/_jsx(PlaceFeatureMap, {
      markers: markers,
      onMove: e => {
        if (e.kind === "feature") {
          setPicked({
            kind: "move",
            latitude: e.latitude,
            longitude: e.longitude
          });
        }
      }
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex h-full items-center justify-center bg-gray-50 text-xs text-gray-500",
      children: "Laddar karta\u2026"
    });
    $[109] = markers;
    $[110] = t40;
  } else {
    t40 = $[110];
  }
  let t41;
  if ($[111] !== hasSavedLocation || $[112] !== isDirty) {
    t41 = !hasSavedLocation && !isDirty && /*#__PURE__*/_jsxs("div", {
      className: "pointer-events-none absolute inset-x-2 top-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50/95 px-2 py-1.5 text-[11px] text-amber-900 shadow-sm",
      children: [/*#__PURE__*/_jsx(AlertTriangle, {
        className: "mt-0.5 h-3.5 w-3.5 shrink-0"
      }), /*#__PURE__*/_jsxs("span", {
        children: ["Ingen position satt av anv\xE4ndaren. Dra funktionsmark\xF6ren till r\xE4tt plats och klicka ", /*#__PURE__*/_jsx("strong", {
          children: "Ange position"
        }), "."]
      })]
    });
    $[111] = hasSavedLocation;
    $[112] = isDirty;
    $[113] = t41;
  } else {
    t41 = $[113];
  }
  let t42;
  if ($[114] !== t40 || $[115] !== t41) {
    t42 = /*#__PURE__*/_jsxs("div", {
      className: "relative h-[234px] overflow-hidden rounded border",
      children: [t40, t41]
    });
    $[114] = t40;
    $[115] = t41;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== feature.location || $[118] !== isClearing || $[119] !== picked) {
    t43 = isClearing ? "\u2014" : picked?.kind === "move" ? `${picked.latitude.toFixed(6)}, ${picked.longitude.toFixed(6)}` : feature.location ? `${feature.location.latitude.toFixed(6)}, ${feature.location.longitude.toFixed(6)}` : "\u2014";
    $[117] = feature.location;
    $[118] = isClearing;
    $[119] = picked;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  let t44;
  if ($[121] !== t43) {
    t44 = /*#__PURE__*/_jsx("p", {
      className: "text-[10px] text-gray-500",
      children: t43
    });
    $[121] = t43;
    $[122] = t44;
  } else {
    t44 = $[122];
  }
  let t45;
  if ($[123] !== isDirty || $[124] !== isRepositioning) {
    t45 = isDirty && /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      variant: "ghost",
      onClick: () => setPicked(null),
      disabled: isRepositioning,
      className: "h-8 gap-1 px-2 text-xs",
      title: "\xC5terst\xE4ll till sparad position",
      children: [/*#__PURE__*/_jsx(RotateCcw, {
        className: "h-3.5 w-3.5"
      }), "\xC5terst\xE4ll"]
    });
    $[123] = isDirty;
    $[124] = isRepositioning;
    $[125] = t45;
  } else {
    t45 = $[125];
  }
  let t46;
  if ($[126] !== hasSavedLocation || $[127] !== isRepositioning || $[128] !== parentPlaceMeta) {
    t46 = parentPlaceMeta && /*#__PURE__*/_jsxs(DropdownMenu, {
      children: [/*#__PURE__*/_jsx(DropdownMenuTrigger, {
        asChild: true,
        children: /*#__PURE__*/_jsx(Button, {
          size: "sm",
          variant: "ghost",
          disabled: isRepositioning,
          className: "h-8 w-8 p-0",
          title: "Fler \xE5tg\xE4rder",
          children: /*#__PURE__*/_jsx(MoreHorizontal, {
            className: "h-4 w-4"
          })
        })
      }), /*#__PURE__*/_jsxs(DropdownMenuContent, {
        align: "end",
        className: "w-56",
        children: [/*#__PURE__*/_jsxs(DropdownMenuItem, {
          onClick: () => setPicked({
            kind: "move",
            latitude: parentPlaceMeta.location.latitude,
            longitude: parentPlaceMeta.location.longitude
          }),
          className: "gap-2",
          children: [/*#__PURE__*/_jsx(Crosshair, {
            className: "h-3.5 w-3.5"
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex flex-col",
            children: [/*#__PURE__*/_jsx("span", {
              children: "Samma som platsen"
            }), /*#__PURE__*/_jsx("span", {
              className: "text-[10px] text-gray-500",
              children: "Flyttar mark\xF6ren till platsens position"
            })]
          })]
        }), /*#__PURE__*/_jsxs(DropdownMenuItem, {
          disabled: !hasSavedLocation,
          onClick: () => setPicked({
            kind: "clear"
          }),
          className: "gap-2 text-red-600 focus:text-red-600",
          children: [/*#__PURE__*/_jsx(Eraser, {
            className: "h-3.5 w-3.5"
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex flex-col",
            children: [/*#__PURE__*/_jsx("span", {
              children: "Rensa position"
            }), /*#__PURE__*/_jsx("span", {
              className: "text-[10px] text-gray-500",
              children: "Stegas som \"utan position\", spara f\xF6r att bekr\xE4fta"
            })]
          })]
        })]
      })]
    });
    $[126] = hasSavedLocation;
    $[127] = isRepositioning;
    $[128] = parentPlaceMeta;
    $[129] = t46;
  } else {
    t46 = $[129];
  }
  const t47 = isDirty ? "default" : "outline";
  let t48;
  if ($[130] !== onReposition || $[131] !== picked) {
    t48 = () => {
      if (!picked) {
        return;
      }
      if (picked.kind === "clear") {
        onReposition(null);
      } else {
        onReposition({
          latitude: picked.latitude,
          longitude: picked.longitude
        });
      }
    };
    $[130] = onReposition;
    $[131] = picked;
    $[132] = t48;
  } else {
    t48 = $[132];
  }
  const t49 = !isDirty || isRepositioning;
  const t50 = isDirty && (isClearing ? "bg-red-600 hover:bg-red-700" : "bg-brand hover:bg-brand/90");
  let t51;
  if ($[133] !== t50) {
    t51 = cn("h-8 gap-1.5", t50);
    $[133] = t50;
    $[134] = t51;
  } else {
    t51 = $[134];
  }
  let t52;
  if ($[135] !== isRepositioning) {
    t52 = isRepositioning && /*#__PURE__*/_jsx(Loader2, {
      className: "h-3.5 w-3.5 animate-spin"
    });
    $[135] = isRepositioning;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  const t53 = isClearing ? "Rensa position" : hasSavedLocation ? "Spara position" : "Ange position";
  let t54;
  if ($[137] !== t47 || $[138] !== t48 || $[139] !== t49 || $[140] !== t51 || $[141] !== t52 || $[142] !== t53) {
    t54 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      variant: t47,
      onClick: t48,
      disabled: t49,
      className: t51,
      children: [t52, t53]
    });
    $[137] = t47;
    $[138] = t48;
    $[139] = t49;
    $[140] = t51;
    $[141] = t52;
    $[142] = t53;
    $[143] = t54;
  } else {
    t54 = $[143];
  }
  let t55;
  if ($[144] !== t45 || $[145] !== t46 || $[146] !== t54) {
    t55 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-1.5",
      children: [t45, t46, t54]
    });
    $[144] = t45;
    $[145] = t46;
    $[146] = t54;
    $[147] = t55;
  } else {
    t55 = $[147];
  }
  let t56;
  if ($[148] !== t44 || $[149] !== t55) {
    t56 = /*#__PURE__*/_jsxs("div", {
      className: "mt-2 flex items-center justify-between gap-2",
      children: [t44, t55]
    });
    $[148] = t44;
    $[149] = t55;
    $[150] = t56;
  } else {
    t56 = $[150];
  }
  let t57;
  if ($[151] !== t39 || $[152] !== t42 || $[153] !== t56) {
    t57 = /*#__PURE__*/_jsxs("div", {
      className: "min-w-0 flex-1",
      children: [t39, t42, t56]
    });
    $[151] = t39;
    $[152] = t42;
    $[153] = t56;
    $[154] = t57;
  } else {
    t57 = $[154];
  }
  let t58;
  if ($[155] !== t34 || $[156] !== t57) {
    t58 = /*#__PURE__*/_jsx("section", {
      className: "px-5 py-4",
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex gap-4",
        children: [t34, t57]
      })
    });
    $[155] = t34;
    $[156] = t57;
    $[157] = t58;
  } else {
    t58 = $[157];
  }
  let t59;
  if ($[158] !== feature.media[0]) {
    t59 = feature.media[0] ? {
      url: feature.media[0].url
    } : null;
    $[158] = feature.media[0];
    $[159] = t59;
  } else {
    t59 = $[159];
  }
  let t60;
  if ($[160] !== feature.assessment || $[161] !== feature.createdAt || $[162] !== feature.creator || $[163] !== feature.featureType.name || $[164] !== feature.id || $[165] !== t59) {
    t60 = /*#__PURE__*/_jsx("section", {
      className: "border-t",
      children: /*#__PURE__*/_jsx(EditableAssessment, {
        feature: {
          id: feature.id,
          createdAt: feature.createdAt,
          featureTypeName: feature.featureType.name,
          creator: feature.creator,
          media: t59,
          assessment: feature.assessment
        }
      })
    });
    $[160] = feature.assessment;
    $[161] = feature.createdAt;
    $[162] = feature.creator;
    $[163] = feature.featureType.name;
    $[164] = feature.id;
    $[165] = t59;
    $[166] = t60;
  } else {
    t60 = $[166];
  }
  let t61;
  if ($[167] !== featureId) {
    t61 = /*#__PURE__*/_jsx(AuditHistoryAccordion, {
      entityType: "feature",
      entityId: featureId
    });
    $[167] = featureId;
    $[168] = t61;
  } else {
    t61 = $[168];
  }
  const t62 = `${feature.featureType.name} — ${feature.place.name}`;
  let t63;
  if ($[169] !== onCloseRejectDialog || $[170] !== onReject) {
    t63 = reason => {
      onReject(reason);
      onCloseRejectDialog();
    };
    $[169] = onCloseRejectDialog;
    $[170] = onReject;
    $[171] = t63;
  } else {
    t63 = $[171];
  }
  let t64;
  if ($[172] !== isRejecting || $[173] !== onCloseRejectDialog || $[174] !== rejectDialogOpen || $[175] !== t62 || $[176] !== t63) {
    t64 = /*#__PURE__*/_jsx(RejectDialog, {
      open: rejectDialogOpen,
      entityType: "feature",
      targetName: t62,
      isSubmitting: isRejecting,
      onClose: onCloseRejectDialog,
      onSubmit: t63
    });
    $[172] = isRejecting;
    $[173] = onCloseRejectDialog;
    $[174] = rejectDialogOpen;
    $[175] = t62;
    $[176] = t63;
    $[177] = t64;
  } else {
    t64 = $[177];
  }
  let t65;
  if ($[178] !== t30 || $[179] !== t58 || $[180] !== t60 || $[181] !== t61 || $[182] !== t64) {
    t65 = /*#__PURE__*/_jsxs("div", {
      className: "flex h-full flex-col overflow-y-auto",
      children: [t30, t58, t60, t61, t64]
    });
    $[178] = t30;
    $[179] = t58;
    $[180] = t60;
    $[181] = t61;
    $[182] = t64;
    $[183] = t65;
  } else {
    t65 = $[183];
  }
  return t65;
}